import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Radio, Table, TableColumnsType, Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { uniqueId } from 'lodash';
import { getOperation, actionOperation } from '@/adapters/master';
import { IApiResponse, IOperationRes } from '@/interfaces/master';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

type Mode = 'insert' | 'update';
type Category = 'watering' | 'fertilizing' | 'chemical' | 'harvest' | 'pestControl' | 'survey';

// Helper to extract error message from unknown errors
const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const OperationPage: React.FC = () => {
  const [tab, setTab] = useState<Category>('watering');
  const [mode, setMode] = useState<Mode>('insert');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);

  const { data, refetch, isFetching } = useQuery<IOperationRes, Error>({
    queryKey: ['operation-list'],
    queryFn: getOperation,
    retry: 0,
  });

  useEffect(() => {
    refetch();
  }, [tab, mode, refetch]);

  useEffect(() => {
    const dataKey = `${tab}_${mode}` as keyof IOperationRes;
    const list = data?.[dataKey] || [];
    setSelected(list.map((item) => ({ ...item, _key: uniqueId('stk-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, mode, data]);

  const getFieldKeys = () => {
    if (tab === 'watering') {
      return {
        id: 'V_WATER_ID',
        date: 'V_WATER_DATE',
        list: 'V_WATER_TRNBR',
        plantKeys: ['V_WATER_ITEM', 'V_WATER_DESC'],
        farmer: 'V_WATER_USER',
        operation: 'V_WATER_ACT',
        tumbol: 'V_WATER_TUMBOL',
        amphur: 'V_WATER_AMPHUR',
        province: 'V_WATER_PROVINCE',
      };
    } else if (tab === 'fertilizing') {
      return {
        id: 'V_FERT_ID',
        date: 'V_FERT_DATE',
        list: 'V_FERT_TRNBR',
        plantKeys: ['V_FERT_ITEM', 'V_FERT_DESC'],
        farmer: 'V_FERT_USER',
        operation: 'V_FERT_ACT',
        tumbol: 'V_FERT_TUMBOL',
        amphur: 'V_FERT_AMPHUR',
        province: 'V_FERT_PROVINCE',
      }
    } else if (tab === 'chemical') {
      return {
        id: 'V_CHEM_ID',
        date: 'V_CHEM_DATE',
        list: 'V_CHEM_TRNBR',
        plantKeys: ['V_CHEM_ITEM', 'V_CHEM_DESC'],
        farmer: 'V_CHEM_USER',
        operation: 'V_CHEM_ACT',
        tumbol: 'V_CHEM_TUMBOL',
        amphur: 'V_CHEM_AMPHUR',
        province: 'V_CHEM_PROVINCE',
      }
    } else if (tab === 'harvest') {
      return {
        id: 'V_HARV_ID',
        date: 'V_HARV_DATE',
        list: 'V_HARV_TRNBR',
        plantKeys: ['V_HARV_ITEM', 'V_HARV_DESC'],
        farmer: 'V_HARV_USER',
        operation: 'V_HARV_ACT',
        tumbol: 'V_HARV_TUMBOL',
        amphur: 'V_HARV_AMPHUR',
        province: 'V_HARV_PROVINCE',
      }
    } else if (tab === 'pestControl') {
      return {
        id: 'V_PD_ID',
        date: 'V_PD_DATE',
        list: 'V_PD_TRNBR',
        plantKeys: ['V_PD_ITEM', 'V_PD_DESC'],
        farmer: 'V_PD_USER',
        operation: 'V_PD_ACT',
        tumbol: 'V_PD_TUMBOL',
        amphur: 'V_PD_AMPHUR',
        province: 'V_PD_PROVINCE',
      }
    } else if (tab === 'survey') {
      return {
        id: 'V_PDR_ID',
        date: 'V_PDR_DATE',
        list: 'V_PDR_TRNBR',
        plantKeys: ['V_PDR_ITEM', 'V_PDR_DESC'],
        farmer: 'V_PDR_USER',
        operation: 'V_PDR_ACT',
        tumbol: 'V_PDR_TUMBOL',
        amphur: 'V_PDR_AMPHUR',
        province: 'V_PDR_PROVINCE',
      }
    } else {
      return {
        id: 'V_WATER_ID',
        date: 'V_WATER_DATE',
        list: 'V_WATER_TRNBR',
        plantKeys: ['V_WATER_ITEM', 'V_WATER_DESC'],
        farmer: 'V_WATER_USER',
        operation: 'V_WATER_ACT',
        tumbol: 'V_WATER_TUMBOL',
        amphur: 'V_WATER_AMPHUR',
        province: 'V_WATER_PROVINCE',
      };
    }
  }

  const keys = getFieldKeys();

  // Notification logic for batch processing
  const notifyBatchErrors = (
    title: string,
    batchResults: IBatchRejection[],
    originalData: any[],
  ) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          // Use the Plant ID (first key in plantKeys) as the identifier in the error list
          const failedId = originalData[index]?.[keys.plantKeys[0]] || `Index: ${index}`;
          errors.push(
            `${failedId}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`,
          );
        }
      });
    }

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul style={{ paddingLeft: '15px', margin: 0, maxHeight: '250px', overflowY: 'auto' }}>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
        duration: 8,
      });
      return true;
    }
    return false;
  };

  const stockMutation = useMutation({
    mutationFn: async ({ action, payload }: { action: 'Create' | 'Update' | 'Delete'; payload: any[] }) => {
      const categoryMap: Record<Category, 'Watering' | 'Fertilizing' | 'Chemical' | 'Harvest' | 'PestControl' | 'Survey'> = {
        watering: 'Watering',
        fertilizing: 'Fertilizing',
        chemical: 'Chemical',
        harvest: 'Harvest',
        pestControl: 'PestControl',
        survey: 'Survey',
      };
      return (await actionOperation(categoryMap[tab], action, payload)) as IApiResponse;
    },
    onSuccess: (res, variables) => {
      const isDelete = variables.action === 'Delete';
      const successMsg = isDelete ? 'ลบข้อมูลสำเร็จ' : (mode === 'insert' ? 'Sync ข้อมูลสำเร็จ' : 'แก้ไขข้อมูลสำเร็จ');
      const failTitle = isDelete ? 'ลบข้อมูลไม่สำเร็จ' : (mode === 'insert' ? 'Sync ข้อมูลไม่สำเร็จ' : 'แก้ไขข้อมูลไม่สำเร็จ');

      if (!notifyBatchErrors(failTitle, res.data as IBatchRejection[], variables.payload)) {
        notification.success({ message: successMsg });
      }
      refetch();
    },
    onError: (err) => notification.error({ message: getErrorMessage(err) }),
  });

  const getPlantDisplay = (record: any) => {
    const k1 = record[keys.plantKeys[0]] || '';
    const k2 = record[keys.plantKeys[1]] || '';
    const combined = `${k1}, ${k2}`.trim();
    return combined || '-';
  };

  const getColumnSearchProps = (dataIndex: string | string[]) => ({
    filters: Array.from(
      new Set(selected.map((item) => {
        if (Array.isArray(dataIndex)) {
          const combined = `${item[dataIndex[0]] || ''} ${item[dataIndex[1]] || ''}`.trim();
          return combined || '-';
        }
        return item[dataIndex] ? String(item[dataIndex]) : '-';
      }))
    ).filter(Boolean).map((val) => ({ text: val, value: val })),
    filterSearch: true,
    onFilter: (value: any, record: any) => {
      let val = '';
      if (Array.isArray(dataIndex)) {
        val = `${record[dataIndex[0]] || ''} ${record[dataIndex[1]] || ''}`.trim() || '-';
      } else {
        val = record[dataIndex] ? String(record[dataIndex]) : '-';
      }
      return val.toLowerCase().includes(String(value).toLowerCase());
    },
  });

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'ID',
        dataIndex: keys.id,
        width: 100,
        sorter: (a, b) => String(a[keys.id] || '').localeCompare(String(b[keys.id] || '')),
        ...getColumnSearchProps(keys.id),
      },
      {
        title: 'วันที่',
        dataIndex: keys.date,
        width: 110,
        render: (text) => (text ? new Date(text).toLocaleDateString('th-TH') : '-'),
        sorter: (a, b) => (a[keys.date] || '').localeCompare(b[keys.date] || ''),
        ...getColumnSearchProps(keys.date),
      },
      {
        title: 'เลขที่รายการ',
        dataIndex: keys.list,
        width: 120,
        sorter: (a, b) => (Number(a[keys.list]) || 0) - (Number(b[keys.list]) || 0),
        ...getColumnSearchProps(keys.list),
      },
      {
        title: 'ปลูก',
        // width: 220,
        render: (_, record) => getPlantDisplay(record),
        sorter: (a, b) => getPlantDisplay(a).localeCompare(getPlantDisplay(b)),
        ...getColumnSearchProps(keys.plantKeys),
      },
      {
        title: 'เกษตรกร',
        dataIndex: keys.farmer,
        // width: 130,
        render: (v) => <b>{v ?? '-'}</b>,
        sorter: (a, b) => String(a[keys.farmer] || '').localeCompare(String(b[keys.farmer] || '')),
        ...getColumnSearchProps(keys.farmer),
      },
      {
        title: 'กิจกรรม',
        dataIndex: keys.operation,
        minWidth: 150,
        ...getColumnSearchProps(keys.operation),
        render: (v) => (v ? String(v) : '-'),
      },
        {
        title: 'ตำบลที่ตั้งสวน',
        dataIndex: keys.tumbol,
        minWidth: 150,
        ...getColumnSearchProps(keys.tumbol),
        render: (v) => (v ? String(v) : '-'),
      },
        {
        title: 'อำเภอที่ตั้งสวน',
        dataIndex: keys.amphur,
        minWidth: 150,
        ...getColumnSearchProps(keys.amphur),
        render: (v) => (v ? String(v) : '-'),
      },
        {
        title: 'จังหวัดที่ตั้งสวน',
        dataIndex: keys.province,
        minWidth: 150,
        ...getColumnSearchProps(keys.province),
        render: (v) => (v ? String(v) : '-'),
      },
    ],
    [tab, keys, selected],
  );

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lower = searchText.toLowerCase();
    return selected.filter((item: any) => {
      const matchesStandard = Object.values(item).some(val =>
        val !== null && typeof val !== 'object' && String(val).toLowerCase().includes(lower)
      );
      const matchesPlant = getPlantDisplay(item).toLowerCase().includes(lower);
      return matchesStandard || matchesPlant;
    });
  }, [selected, searchText, keys]);

  const isPending = stockMutation.isPending;

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex vertical gap={4}>
          <Radio.Group value={tab} onChange={(e) => setTab(e.target.value)} buttonStyle="solid">
            <Radio.Button value="watering">Watering (ให้น้ำ)</Radio.Button>
            <Radio.Button value="fertilizing">Fertilizing (ปุ๋ย)</Radio.Button>
            <Radio.Button value="chemical">Chemical (เคมี)</Radio.Button>
            <Radio.Button value="harvest">Harvest (เก็บเกี่ยว)</Radio.Button>
            <Radio.Button value="pestControl">Pest Control (กำจัดศัตรูพืช)</Radio.Button>
            <Radio.Button value="survey">Survey (สำรวจ)</Radio.Button>
          </Radio.Group>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value as Mode)} size="small">
            <Radio.Button value="insert">รายการรอนำเข้า (Add)</Radio.Button>
            <Radio.Button value="update">รายการแก้ไข/ลบ (Edit/Delete)</Radio.Button>
          </Radio.Group>
        </Flex>

        <Flex gap={12} align="flex-end">
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            รีเฟรช
          </Button>
          <Input.Search
            placeholder="ค้นหา..."
            allowClear
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          {mode === 'update' && (
            <Button
              danger
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => stockMutation.mutate({ action: 'Delete', payload: selectedRows })}
            >
              ลบข้อมูล
            </Button>
          )}
          <Button
            type="primary"
            style={{ minWidth: 150, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            disabled={!selectedRowKeys.length || isPending}
            onClick={() =>
              stockMutation.mutate({
                action: mode === 'insert' ? 'Create' : 'Update',
                payload: selectedRows,
              })
            }
          >
            {isPending ? 'กำลังดำเนินการ...' : mode === 'insert' ? 'ส่งข้อมูลไป OTOD >' : 'แก้ไขข้อมูลที่ OTOD >'}
          </Button>
        </Flex>
      </Flex>

      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 'max-content' }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default OperationPage;